import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

import { Avatar, Button, Drawer, IconButton } from '@mui/material';
import cn from 'classnames';
import { KeyRound, LogIn, LogOut, Menu, X } from 'lucide-react';
import { observer } from 'mobx-react-lite';

import authStore from '../../store/auth';
import { playerPath } from '../../utils/playerPath';

import styles from './Header.module.scss';

const links = [
    { to: '/matches', label: 'Матчи' },
    { to: '/statistics', label: 'Статистика' },
    { to: '/players', label: 'Игроки' },
    { to: '/heroes', label: 'Герои' },
    { to: '/randomizer', label: 'Генератор команд', adminOnly: true },
];

const roleLabels = { owner: 'Главный админ', admin: 'Админ', player: 'Игрок' };

const navLinkClassName = ({ isActive }: { isActive: boolean }) =>
    cn(styles.link, { [styles.active]: isActive });

const Header = observer(function () {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const { pathname } = useLocation();

    // Закрываем мобильное меню при переходе
    useEffect(() => setIsMenuOpen(false), [pathname]);

    const { user, profile } = authStore;
    const visibleLinks = links.filter(
        ({ adminOnly }) => !adminOnly || authStore.isAdmin
    );

    const authButton = authStore.isAuthorized ? (
        <>
            {profile ? (
                <Link
                    to={playerPath(profile._id, profile.login)}
                    className={styles.profile}
                    title='Мой профиль'
                >
                    <span className={styles.account}>
                        <span className={styles.accountLogin}>
                            {profile.nickname}
                        </span>
                        {user?.role && (
                            <span className={styles.accountRole}>
                                {roleLabels[user.role]}
                            </span>
                        )}
                    </span>
                    <Avatar
                        src={profile.avatar || ''}
                        alt={profile.nickname}
                        sx={{ width: 32, height: 32 }}
                    />
                </Link>
            ) : (
                // Аккаунт без привязанного игрока — показываем логин
                user?.role && (
                    <span className={styles.account}>
                        <span className={styles.accountLogin}>
                            {user.login ?? user.email}
                        </span>
                        <span className={styles.accountRole}>
                            {roleLabels[user.role]}
                        </span>
                    </span>
                )
            )}
            <IconButton
                component={Link}
                to='/change-password'
                aria-label='Сменить пароль'
                title='Сменить пароль'
            >
                <KeyRound size={18} />
            </IconButton>
            <Button
                color='inherit'
                startIcon={<LogOut size={16} />}
                onClick={() => authStore.logout()}
            >
                Выйти
            </Button>
        </>
    ) : (
        <Button
            component={Link}
            to='/login'
            variant='outlined'
            color='secondary'
            startIcon={<LogIn size={16} />}
        >
            Войти
        </Button>
    );

    return (
        <header className={styles.header}>
            <div className={styles.inner}>
                <Link to='/' className={styles.logo}>
                    <span className={styles.logoMark}>5×5</span>
                    <span className={styles.logoText}>DOTA</span>
                </Link>

                <nav className={styles.nav} aria-label='Основная навигация'>
                    {visibleLinks.map(({ to, label }) => (
                        <NavLink key={to} to={to} className={navLinkClassName}>
                            {label}
                        </NavLink>
                    ))}
                </nav>

                <div className={styles.auth}>{authButton}</div>

                <IconButton
                    className={styles.burger}
                    onClick={() => setIsMenuOpen(true)}
                    aria-label='Открыть меню'
                >
                    <Menu />
                </IconButton>
            </div>

            <Drawer
                anchor='right'
                open={isMenuOpen}
                onClose={() => setIsMenuOpen(false)}
                PaperProps={{ className: styles.drawer }}
            >
                <IconButton
                    className={styles.drawerClose}
                    onClick={() => setIsMenuOpen(false)}
                    aria-label='Закрыть меню'
                >
                    <X />
                </IconButton>
                <nav className={styles.drawerNav}>
                    {visibleLinks.map(({ to, label }) => (
                        <NavLink key={to} to={to} className={navLinkClassName}>
                            {label}
                        </NavLink>
                    ))}
                </nav>
                <div className={styles.drawerAuth}>{authButton}</div>
            </Drawer>
        </header>
    );
});

export default Header;
